import { Calendar, Play, Plus } from "lucide-react";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Asset } from "@/app/dashboard/transactions/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as m from "@/paraglide/messages";
import { getDashboardHoldings } from "@/services/controllers/dashboard";
import {
  createPaperTransaction,
  getPaperDatePrice,
} from "@/services/controllers/trade";
import { useAppSelector } from "@/services/hooks/redux.hooks";

import SearchCoin from "./coinInput";
import PairInput from "./pairInput";
import LoadingSpinner from "../loading-spinner/loading-spinner";
import { Calendar as CalendarView } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useToast } from "../ui/use-toast";

const AddPaperTransaction: FC<{}> = ({}) => {
  const { selectedExchange } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { toast } = useToast();

  const [selectedType, setSelectedType] = useState<"buy" | "sell" | undefined>(
    "buy",
  );
  const [selectedCoin, setSelectedCoin] = useState<Asset>();
  const [selectedPair, setSelectedPair] = useState<any>({});
  const [time, setTime] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [fees, setFees] = useState<string>("0");
  const [affectUSDT, setAffectUSDT] = useState<boolean>(false);
  const [dateTemp, setDateTemp] = useState<string>("");
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (showDialog) {
      setFees("0");
      setDateTemp(new Date().toISOString().split("T")[0]);
      setTimeout(() => {
        const dateInput: any = document.getElementById("date-input");
        dateInput.value = new Date().toISOString().split("T")[0];
      }, 500);
      setTime(new Date().toTimeString().split(" ")[0].slice(0, -3));
      if (selectedCoin?.asset_data?.current_price != null)
        setPrice(selectedCoin.asset_data.current_price.toString());
      else setPrice("");
    } else {
      setPrice("");
      setAmount("");
      setFees("");
      setAffectUSDT(false);
    }
  }, [selectedCoin, showDialog]);

  useEffect(() => {
    const validYear = parseInt(dateTemp.split("-")?.[0]).toString().length > 3;
    if (selectedCoin?.asset_data?.symbol && validYear) {
      getPaperDatePrice(
        selectedCoin?.asset_data.symbol.toUpperCase(),
        dateTemp,
        selectedPair?.base_asset ?? "USDT",
      ).then((response: any) => {
        if (response.data.data.price) {
          setPrice(parseFloat(response.data.data.price).toString());
        } else {
          setPrice("");
        }
      });
    }
  }, [selectedCoin, selectedPair, dateTemp]);

  const reset = useCallback(() => {
    setSelectedType("buy");
    setSelectedCoin(undefined);
    setSelectedPair(undefined);
    setDateTemp(new Date().toString());
    setTime(new Date().toString());
    setFees("");
    setAmount("");
    setPrice("");
  }, []);

  const onSave = useCallback(() => {
    setIsSaving(true);
    const dateInput: any = document.getElementById("date-input");
    const date = dateInput.value;
    if (
      selectedExchange?.exchange_key_id &&
      date !== "" &&
      date != null &&
      selectedCoin?.asset_data?.symbol
    )
      createPaperTransaction(selectedExchange?.exchange_key_id, {
        amount,
        price: price as any,
        side: selectedType?.toUpperCase() as any,
        settled_at: new Date(`${date}T${time}:00.000Z`) as any,
        asset_name: selectedCoin?.asset_data?.symbol.toUpperCase() as string,
        base_name: selectedPair.base_asset,
        symbol_name: selectedPair.name,
        affect: affectUSDT,
        fees,
      })
        .then(() => {
          toast({
            description: m.watery_even_lizard_express(),
            variant: "success",
            duration: 2000,
          });
          window.dispatchEvent(
            new CustomEvent("refetch-orders", {
              detail: { key: selectedExchange.exchange_key_id },
            }),
          );
          setShowDialog(false);
          getDashboardHoldings({
            params: {
              exchange_key_id: selectedExchange?.exchange_key_id,
            },
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
  }, [
    affectUSDT,
    amount,
    fees,
    price,
    selectedCoin?.asset_data?.symbol,
    selectedExchange,
    selectedPair?.base_asset,
    selectedPair?.name,
    selectedType,
    time,
    toast,
  ]);

  const TypeCTA = useCallback(
    (type: "buy" | "sell") => {
      const isActive = selectedType === type;

      const onClick = () => {
        setSelectedType(type);
      };

      return (
        <button
          className={twMerge(
            isActive
              ? type === "buy"
                ? "opacity-100 bg-success bg-opacity-20 border-success-foreground"
                : "opacity-100 bg-destructive bg-opacity-20 border-destructive-foreground"
              : "opacity-40 bg-transaparent border-gray-200",
            " h-[34px] border-solid border-[1px] rounded-sm px-2",
          )}
          onClick={onClick}
        >
          <div
            className={twMerge(
              isActive
                ? type === "buy"
                  ? "text-success-foreground"
                  : "text-destructive-foreground"
                : "",
              "gap-1 font-bold justify-center text-sm flex items-center",
            )}
          >
            <Play
              className={twMerge(
                type === "buy" ? "-rotate-90" : "rotate-90",
                isActive
                  ? type === "buy"
                    ? "fill-success-foreground"
                    : "fill-destructive-foreground"
                  : "fill-gray-600",
                `w-3 h-3 stroke-0`,
              )}
            />
            {type === "buy" ? m.buy() : m.sell()}
          </div>
        </button>
      );
    },
    [selectedType],
  );

  return (
    <Dialog
      open={showDialog}
      onOpenChange={(op) => {
        setShowDialog(op);
        if (!op) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1 md:me-3">
          {m.add_transaction()}
          <Plus className="h-4 w-4 text-foregroud" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{m.add_transaction()}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex gap-4 items-center justify-center">
            {TypeCTA("buy")}
            {TypeCTA("sell")}
          </div>
          <div className="space-y-2">
            <Label htmlFor="coin">{m.coin()}</Label>
            <SearchCoin onSelectCoin={setSelectedCoin} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="exchange-pair">{m.ExchangePair()}</Label>
            <PairInput
              coin={selectedCoin?.asset_name ?? ""}
              onClick={setSelectedPair}
              disabled={!selectedCoin?.asset_name}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">{m.date()}</Label>
              <div className="relative" id="date">
                <Input
                  type="date"
                  className="pr-10"
                  id="date-input"
                  value={dateTemp}
                  disabled={selectedCoin?.asset_name == null}
                  readOnly
                />
                <Popover open={showCalendar} onOpenChange={setShowCalendar}>
                  <PopoverTrigger
                    asChild
                    disabled={selectedCoin?.asset_name == null}
                  >
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <CalendarView
                      mode="single"
                      selected={new Date(dateTemp)}
                      disabled={selectedCoin?.asset_name == null}
                      toDate={new Date()}
                      onSelect={(date) => {
                        setDateTemp(moment(date).format("YYYY-MM-DD"));
                        setShowCalendar(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">{m.time()}</Label>
              <Input
                id="time"
                className="w-full"
                type="time"
                value={time}
                max={new Date().toString()}
                onChange={(e) => {
                  setTime(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">{m.amount()}</Label>
              <Input
                id="amount"
                placeholder="Enter Amount"
                value={amount}
                type="number"
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="price">{m.price()}</Label>
              <div className="flex">
                <Input
                  id="price"
                  placeholder="Enter Price"
                  className="flex-1 border-r-0 rounded-r-none"
                  value={price}
                  type="number"
                  onChange={(e) => {
                    setPrice(e.target.value);
                  }}
                />
                <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 sm:text-sm">
                  {selectedPair?.base_asset ?? "USD"}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fees">{`Fees ${
              selectedCoin?.asset_data?.symbol && selectedPair?.base_asset
                ? `in ${
                    selectedType === "buy"
                      ? selectedCoin.asset_data.symbol.toUpperCase()
                      : selectedPair.base_asset
                  }`
                : ""
            }`}</Label>
            <Input
              id="fees"
              placeholder="Enter Amount"
              value={fees}
              type="number"
              onChange={(e) => {
                setFees(e.target.value);
              }}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="deduct"
              checked={affectUSDT}
              onCheckedChange={setAffectUSDT as any}
            />
            <Label htmlFor="deduct">
              {selectedType === "buy"
                ? m.deduct_from({
                    base_asset: selectedPair?.base_asset ?? "USDT",
                  })
                : m.add_to({ base_asset: selectedPair?.base_asset ?? "USDT" })}
            </Label>
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            disabled={isSaving}
            onClick={() => setShowDialog(false)}
          >
            {m.cancel()}
          </Button>
          <Button
            disabled={
              isSaving ||
              selectedCoin?.asset_name == null ||
              selectedPair?.base_asset == null ||
              price?.trim() === "" ||
              time?.trim() === "" ||
              dateTemp?.trim() === "" ||
              amount?.trim() === "" ||
              selectedType == null ||
              fees.trim() === ""
            }
            onClick={onSave}
          >
            {isSaving ? <LoadingSpinner /> : m.save()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPaperTransaction;
